import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { formatMoney } from '@theme/money-formatting';

/**
 * Free shipping progress bar for the cart drawer.
 * Ported from callin/assets/theme.js shippingBarProgress().
 */
class CalinCartShippingBar extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onCartUpdate = null;

  /** @type {((event: Event) => void) | null} */
  #onDrawerOpen = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /**
   * Last total we painted with, so we can re-paint instantly (no fetch) when the
   * cart drawer morph wipes our message/progress markup.
   * @type {number | null}
   */
  #lastTotalCents = null;

  /**
   * Last non-empty message HTML we painted. Lets the observer tell an intentional
   * blank apart from a morph wipe, so re-painting can't loop.
   * @type {string}
   */
  #lastMessageHtml = '';

  connectedCallback() {
    const total = Number(this.dataset.cartTotal);
    if (!Number.isNaN(total)) {
      this.#update(total);
    }

    // The cart drawer re-renders (morphs) on every cart change. `data-skip-node-update`
    // keeps this host's class/style, but the section morph still wipes the child
    // message text. Re-paint from the cached total whenever that happens so the bar
    // never blanks out or lags behind while the cart update promise resolves.
    this.#observer = new MutationObserver(() => {
      if (this.#lastTotalCents == null || !this.#lastMessageHtml) return;
      const messageEl = this.querySelector('.calin-cart-shipping-bar__message');
      if (messageEl instanceof HTMLElement && messageEl.childNodes.length === 0) {
        this.#update(this.#lastTotalCents);
      }
    });
    this.#observer.observe(this, { childList: true, characterData: true, subtree: true });

    this.#onCartUpdate = (event) => {
      const cartEvent = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);
      cartEvent.promise?.then(({ cart }) => {
        const amount = cart?.cost?.totalAmount?.amount;
        if (amount == null) return;
        this.#update(Math.round(Number(amount) * 100));
      });
    };

    this.#onDrawerOpen = (event) => {
      const target = /** @type {HTMLElement | null} */ (event.target);
      if (target?.id !== 'cart-drawer') return;

      fetch(`${Theme.routes.cart_url}.js`)
        .then((response) => response.json())
        .then((data) => {
          if (typeof data.total_price === 'number') {
            this.#update(data.total_price);
          }
        })
        .catch(() => {});
    };

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
  }

  disconnectedCallback() {
    if (this.#onCartUpdate) {
      document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    }
    if (this.#onDrawerOpen) {
      document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    }
    this.#observer?.disconnect();
  }

  /**
   * @param {number} totalCents
   */
  #update(totalCents) {
    const shippingRate = Number(this.dataset.shippingRate);
    if (!shippingRate || !this.dataset.shippingMessage) return;

    this.#lastTotalCents = totalCents;

    const currencyRate = Math.round(shippingRate * (window.Shopify?.currency?.rate || 1));
    const totalMajor = totalCents / 100;
    const remainingMajor = currencyRate - totalMajor;

    const messageEl = this.querySelector('.calin-cart-shipping-bar__message');
    const progressEl = this.querySelector('.calin-cart-shipping-bar__progress');

    if (!(messageEl instanceof HTMLElement) || !(progressEl instanceof HTMLElement)) return;

    const escapeHtml = (str) =>
      String(str).replace(
        /[&<>"']/g,
        (char) =>
          ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char
      );

    const isComplete = remainingMajor <= 0;
    let messageHtml = '';

    if (!isComplete) {
      const remainingCents = Math.round(remainingMajor * 100);
      const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      const currency = window.Shopify?.currency?.active || '';
      const priceFormatted = formatMoney(remainingCents, moneyFormat, currency);
      const priceHtml = `<span class="calin-cart-shipping-bar__amount">${escapeHtml(priceFormatted)}</span>`;
      messageHtml = (this.dataset.shippingMessage || '')
        .split('||price||')
        .map((part) => escapeHtml(part))
        .join(priceHtml);
    } else {
      messageHtml = escapeHtml(this.dataset.shippingSuccessMessage || '');
    }

    let percentage = (totalMajor * 100) / currencyRate;
    if (percentage > 10 && percentage < 100) {
      percentage -= 5;
    } else if (percentage > 100) {
      percentage = 100;
    }
    percentage = Math.trunc(percentage);

    this.#lastMessageHtml = messageHtml;
    messageEl.innerHTML = messageHtml;
    this.classList.toggle('is-complete', isComplete);
    this.style.setProperty('--calin-shipping-progress', `${percentage / 100}`);
    progressEl.setAttribute('aria-valuenow', String(percentage));
    this.classList.remove('hidden');
  }
}

if (!customElements.get('calin-cart-shipping-bar')) {
  customElements.define('calin-cart-shipping-bar', CalinCartShippingBar);
}
